<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :companyRules="companyRules"
    :companyIndex="companyIndex"
    :existingCompanies="[]"
    :company_section_title="$t('signup.lt.extra_info.title')"
    :company_section_description="$t('signup.lt.extra_info.description')"
    :existingDataOverride="existingDataOverride"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
    @selectExistingCompany="$emit('selectExistingCompany', $event)"
    @resetCompanySelection="$emit('resetCompanySelection')"
  >
    <template #company="{ locked, companyField }">
      <TextRowComponent
        v-if="userType !== 'private'"
        v-bind="companyField('vat')"
        :disabled="locked"
      />
      <TextRowComponent
        v-bind="companyField('bank_account')"
        :disabled="locked"
      />
      <TextRowComponent v-bind="companyField('bank_name')" :disabled="locked" />
      <TextRowComponent
        v-bind="companyField('bank_swift')"
        :disabled="locked"
      />
    </template>
  </ContactReviewDisplayComponent>
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { isValidIban, useValidators } from "@/composables/useValidators";
import type { CompanyRules } from "@/signup/common/contactFields";
import TextRowComponent from "@/signup/common/TextRowComponent.vue";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";
import type { Account, Company } from "@/types";
import { watch } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    companyIndex?: number;
    userType: "private" | "company" | undefined;
    existingDataOverride?: boolean;
  }>(),
  { existingDataOverride: false }
);

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  resetCompanySelection: [];
}>();

const { t } = useI18n();
const { required, iban } = useValidators(t);

const { selectedCompany } = useCompanyFinder(props.accountData);
const company = selectedCompany.value;

const companyRules: CompanyRules = {
  vat: { required },
  bank_account: { required, iban },
  bank_name: { required },
  bank_swift: { required },
};

watch(
  () => company.bank_account,
  async (newValue) => {
    if (newValue && isValidIban(newValue)) {
      try {
        const response = await goSolidApi.get(
          `/api/utils/iban-validator/${newValue}`
        );
        if (response && response.data && response.data.valid) {
          const update_data = {
            ...company,
            bank_name: response.data.bank_name,
            bank_swift: response.data.swift_code,
          } as Company;
          emit("handleUpdateCompany", update_data);
        }
      } catch {
        // Fail silently if we can't fetch the bank name and swift code
      }
    }
  }
);
</script>

<style scoped lang="scss"></style>
